import type { TabelaSchematu, WynikZapytania } from '@/lib/baza-api';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  PORÓWNANIE WYNIKU Z DRUGĄ BAZĄ — to samo zapytanie na dwóch bazach, wiersz po wierszu.
 *
 *  ⚠️⚠️ ZAMÓWIONE: „spoko, że możemy zrobić selecta na bazie na maszynie, ale też musi być tak,
 *  żeby potem nie było rozjazdów" (28.09.2026). To jest narzędzie, którym się je WIDZI: przy
 *  wybranej bazie maszyny każdy wiersz mówi, czy zgadza się z bazą porównania (zwykle master).
 *
 *  ⚠️ PO KLUCZU, GDY SIĘ DA. Zapytanie z jednej tabeli, w którego wyniku stoją wszystkie kolumny
 *  jej klucza głównego, porównujemy po kluczu — wtedy „inne" wskazuje konkretne komórki. W każdym
 *  innym wypadku porównujemy CAŁE wiersze, więc wiersz z inną wartością wychodzi jako para
 *  „tylko tu" + „tylko tam" — mniej wygodnie, ale nadal prawdziwie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

export type StanWiersza = 'ZGODNY' | 'INNY' | 'TYLKO_TU' | 'BRAK_TU';

export type WierszPorownania = {
  stan: StanWiersza;
  /** Wiersz z tej bazy; przy `BRAK_TU` — wiersz z bazy porównania. */
  wiersz: (string | null)[];
  /** Przy `INNY`: indeksy kolumn, które się różnią, i wartości z bazy porównania. */
  rozne?: number[];
  tam?: (string | null)[];
};

export type Porownanie =
  | { ok: false; powod: 'INNE_KOLUMNY' }
  | { ok: true; poKluczu: boolean; wiersze: WierszPorownania[]; liczby: Record<StanWiersza, number>; niepelne: boolean };

/**
 * TABELA Z ZAPYTANIA — jedyna po FROM, bez łączeń. Dla czegokolwiek bardziej złożonego `null`,
 * i wtedy porównanie idzie po całych wierszach.
 */
export function tabelaZapytania(sql: string, tabele: readonly TabelaSchematu[]): TabelaSchematu | null {
  const bezKomentarzy = sql.replace(/--.*$/gm, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  if (/\bjoin\b/i.test(bezKomentarzy)) return null;
  const from = [...bezKomentarzy.matchAll(/\bfrom\s+(?:\[?dbo\]?\s*\.\s*)?\[?([A-Za-z0-9_]+)\]?/gi)];
  if (from.length !== 1) return null;
  const nazwa = from[0]![1]!.toLowerCase();
  return tabele.find((t) => t.nazwa.toLowerCase() === nazwa) ?? null;
}

export function porownajWyniki(tu: WynikZapytania, tam: WynikZapytania, tabela: TabelaSchematu | null): Porownanie {
  const nazwy = (w: WynikZapytania) => w.kolumny.map((k) => k.nazwa.toLowerCase()).join('|');
  if (nazwy(tu) !== nazwy(tam)) return { ok: false, powod: 'INNE_KOLUMNY' };

  const kluczKolumn = tabela?.kolumny.filter((k) => k.klucz).map((k) => k.nazwa.toLowerCase()) ?? [];
  const idx = kluczKolumn.map((k) => tu.kolumny.findIndex((c) => c.nazwa.toLowerCase() === k));
  const poKluczu = kluczKolumn.length > 0 && idx.every((i) => i >= 0);

  /**
   * ⚠️ KLUCZ PO KOLACJI SPS (CI_AS): bez wielkości liter i bez spacji na końcu — dwa zapisy,
   * które baza uzna za jeden wiersz, nie mogą tu wyjść jako „tylko tu" i „tylko tam".
   */
  const klucz = (w: (string | null)[]): string =>
    poKluczu ? idx.map((i) => (w[i] ?? '\u0000').trim().toLowerCase()).join('\u0001') : JSON.stringify(w);

  const tamWg = new Map<string, (string | null)[][]>();
  for (const w of tam.wiersze) {
    const k = klucz(w);
    const l = tamWg.get(k);
    if (l === undefined) tamWg.set(k, [w]);
    else l.push(w);
  }

  const wiersze: WierszPorownania[] = [];
  for (const w of tu.wiersze) {
    const k = klucz(w);
    const lista = tamWg.get(k);
    const para = lista?.shift();
    if (lista !== undefined && lista.length === 0) tamWg.delete(k);
    if (para === undefined) {
      wiersze.push({ stan: 'TYLKO_TU', wiersz: w });
      continue;
    }
    const rozne = w.map((v, i) => (v !== para[i] ? i : -1)).filter((i) => i >= 0);
    wiersze.push(rozne.length === 0 ? { stan: 'ZGODNY', wiersz: w } : { stan: 'INNY', wiersz: w, rozne, tam: para });
  }
  for (const lista of tamWg.values()) for (const w of lista) wiersze.push({ stan: 'BRAK_TU', wiersz: w });

  const liczby: Record<StanWiersza, number> = { ZGODNY: 0, INNY: 0, TYLKO_TU: 0, BRAK_TU: 0 };
  for (const w of wiersze) liczby[w.stan] += 1;
  /** ⚠️ Obcięty wynik po którejkolwiek stronie: „brak" może znaczyć „za limitem", nie „nie ma". */
  return { ok: true, poKluczu, wiersze, liczby, niepelne: tu.obcieto || tam.obcieto };
}
